/**
 * Page Header Component
 * Displays breadcrumbs, page title, and action buttons
 */

import React from 'react';
import { Breadcrumb, Space } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { spacing, typography, colors } from '../../theme/designTokens';

/**
 * PageHeader Component
 *
 * @param {string} title - Page title
 * @param {string} subtitle - Optional subtitle/description
 * @param {Array} breadcrumbs - Array of breadcrumb items [{label, path}]
 * @param {ReactNode} actions - Action buttons (export, create, etc.)
 * @param {ReactNode} extra - Additional content to display
 */
function PageHeader({
  title,
  subtitle,
  breadcrumbs = [],
  actions,
  extra,
  style = {},
}) {
  const navigate = useNavigate();

  // Build breadcrumb items
  const breadcrumbItems = [
    {
      title: (
        <span onClick={() => navigate('/trustgrid/dashboard')} style={{ cursor: 'pointer' }}>
          <HomeOutlined />
        </span>
      ),
    },
    ...breadcrumbs.map((crumb, index) => ({
      title: crumb.path ? (
        <span
          onClick={() => navigate(crumb.path)}
          style={{ cursor: 'pointer' }}
        >
          {crumb.label}
        </span>
      ) : (
        crumb.label
      ),
    })),
  ];

  return (
    <div
      style={{
        marginBottom: spacing.lg,
        ...style,
      }}
    >
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Breadcrumb
          items={breadcrumbItems}
          style={{ marginBottom: spacing.xs }}
        />
      )}

      {/* Title and Actions Row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: subtitle || extra ? spacing.sm : 0,
        }}
      >
        {/* Title section */}
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontSize: typography.headings.h3.fontSize,
              fontWeight: typography.headings.h3.fontWeight,
              lineHeight: typography.headings.h3.lineHeight,
              margin: 0,
              color: colors.text.primary,
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary,
                margin: `${spacing.xs}px 0 0 0`,
              }}
            >
              {subtitle}
            </p>
          )}
        </div>

        {/* Actions section */}
        {actions && (
          <Space size="middle" style={{ marginLeft: spacing.lg }}>
            {actions}
          </Space>
        )}
      </div>

      {/* Extra content */}
      {extra && (
        <div style={{ marginTop: spacing.md }}>
          {extra}
        </div>
      )}
    </div>
  );
}

export default PageHeader;
